import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Lock, Play, ChevronDown, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import FeedbackQuestion from "@/components/shared/FeedbackQuestion";

interface LessonData {
  id: string;
  title: string;
  description: string | null;
  video_url: string | null;
  image_url: string | null;
  lesson_order: number;
  course_id: string;
  group_name: string | null;
}

interface CourseData {
  id: string;
  title: string;
}

interface LessonGroup {
  name: string;
  lessons: LessonData[];
}

const CourseLesson = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const [course, setCourse] = useState<CourseData | null>(null);
  const [lessons, setLessons] = useState<LessonData[]>([]);
  const [lessonGroups, setLessonGroups] = useState<LessonGroup[]>([]);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [currentLesson, setCurrentLesson] = useState<LessonData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (!courseId) return;

    const fetchCourseAndLessons = async () => {
      setIsLoading(true);
      try {
        const { data: courseData, error: courseError } = await supabase
          .from('courses')
          .select('id, title')
          .eq('id', courseId)
          .single();
        
        if (courseError) throw courseError;
        setCourse(courseData);
        
        const { data: lessonsData, error: lessonsError } = await supabase
          .from('course_lessons')
          .select('*')
          .eq('course_id', courseId)
          .order('lesson_order', { ascending: true });
          
        if (lessonsError) throw lessonsError;
        
        if (lessonsData && lessonsData.length > 0) {
          setLessons(lessonsData);
          setCurrentLesson(lessonsData[currentLessonIndex]);
          
          const groups: Record<string, LessonData[]> = {};
          lessonsData.forEach((lesson: LessonData) => {
            const groupName = lesson.group_name || "Ungrouped";
            if (!groups[groupName]) {
              groups[groupName] = [];
            }
            groups[groupName].push(lesson);
          });

          const groupsArray = Object.entries(groups).map(([name, lessons]) => ({
            name,
            lessons
          }));
          
          setLessonGroups(groupsArray);
          
          const expanded: Record<string, boolean> = {};
          groupsArray.forEach(group => {
            expanded[group.name] = true;
          });
          setExpandedGroups(expanded);
        }
      } catch (error: any) {
        console.error('Error fetching course data:', error.message);
        toast({
          title: "Failed to load course",
          description: error.message,
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourseAndLessons();
    
    if (user) {
      checkEnrollment();
    } else {
      setIsEnrolled(false);
    }
  }, [courseId, currentLessonIndex, user, toast]);

  const checkEnrollment = async () => {
    if (!user || !courseId) return;

    try {
      const { data, error } = await supabase
        .from('course_enrollments')
        .select('*')
        .eq('course_id', courseId)
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking enrollment:', error);
        return;
      }

      setIsEnrolled(!!data);
    } catch (error) {
      console.error('Error checking enrollment:', error);
    }
  };

  const selectLesson = (lesson: LessonData) => {
    if (isEnrolled) {
      const index = lessons.findIndex(l => l.id === lesson.id);
      if (index !== -1) {
        setCurrentLessonIndex(index);
        setCurrentLesson(lesson);
      }
    } else {
      toast({
        title: "Enrollment required",
        description: "Please enroll in this course to access all lessons",
        variant: "destructive"
      });
    }
  };

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const getYouTubeEmbedUrl = (url: string) => {
    if (!url) return '';
    
    if (url.includes('youtube.com/watch')) {
      const videoId = new URL(url).searchParams.get('v');
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1].split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('youtube.com/embed/')) {
      return url;
    }
    
    return url;
  };

  const getVimeoEmbedUrl = (url: string) => {
    if (!url) return '';

    if (url.includes('vimeo.com/')) {
      const vimeoRegex = /vimeo\.com\/(?:video\/)?(\d+)/;
      const match = url.match(vimeoRegex);
      if (match && match[1]) {
        return `https://player.vimeo.com/video/${match[1]}`;
      }
    } else if (url.includes('player.vimeo.com/video/')) {
      return url;
    }
    
    return url;
  };

  const isVimeoUrl = (url: string) => {
    return url && (url.includes('vimeo.com') || url.includes('player.vimeo.com'));
  };

  const isYouTubeUrl = (url: string) => {
    return url && (url.includes('youtube.com') || url.includes('youtu.be'));
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-10 bg-gray-200 rounded w-3/4"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!isEnrolled) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
          <Link to={`/courses/${courseId}`}>
            <Button variant="ghost" className="mb-6 pl-0 hover:bg-transparent">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to course
            </Button>
          </Link>
          <Card className="p-8 text-center">
            <h3 className="text-xl font-semibold mb-3">Course Access Required</h3>
            <p className="text-gray-600 mb-6">Please enroll in this course to access the lessons</p>
            <Link to={`/courses/${courseId}`}>
              <Button className="w-full max-w-xs">Go to Course Page</Button>
            </Link>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={course?.title ? `${course.title} - Lessons` : "Course Lessons"}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <Link to={`/courses/${courseId}`}>
          <Button variant="ghost" className="mb-6 pl-0 hover:bg-transparent">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to course
          </Button>
        </Link>
        
        {!currentLesson ? (
          <Card className="p-6 text-center">
            <h3 className="text-xl font-semibold mb-4">No lessons available</h3>
            <p className="text-gray-500">This course doesn't have any lessons yet.</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <div className="space-y-4">
                <Card className="overflow-hidden">
                  {currentLesson.video_url ? (
                    <div className="aspect-video">
                      {isYouTubeUrl(currentLesson.video_url) ? (
                        <iframe
                          src={getYouTubeEmbedUrl(currentLesson.video_url)}
                          title={currentLesson.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      ) : isVimeoUrl(currentLesson.video_url) ? (
                        <iframe
                          src={getVimeoEmbedUrl(currentLesson.video_url)}
                          title={currentLesson.title}
                          className="w-full h-full"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      ) : (
                        <video
                          src={currentLesson.video_url}
                          controls
                          className="w-full h-full"
                        ></video>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center aspect-video bg-gray-100">
                      <p className="text-gray-500">No video available for this lesson</p>
                    </div>
                  )}
                </Card>
                
                <div>
                  <h3 className="text-xl font-semibold">{currentLesson.title}</h3>
                  {currentLesson.group_name && (
                    <Badge variant="secondary" className="mt-1">
                      {currentLesson.group_name}
                    </Badge>
                  )}
                  <p className="text-gray-600 mt-2">{currentLesson.description}</p>
                  
                  {currentLesson.id && <FeedbackQuestion itemId={currentLesson.id} itemType="lesson" />}
                </div>
              </div>
            </div>
            
            <div>
              <Card className="p-4">
                <h3 className="font-semibold mb-3 px-2">Lessons</h3>
                <div className="space-y-2">
                  {lessonGroups.length > 0 ? (
                    lessonGroups.map((group) => (
                      <Collapsible 
                        key={group.name} 
                        open={expandedGroups[group.name]} 
                        className="border rounded-md overflow-hidden mb-2"
                      >
                        <CollapsibleTrigger 
                          onClick={() => toggleGroup(group.name)}
                          className="w-full flex items-center justify-between p-2 hover:bg-gray-50"
                        >
                          <span className="font-medium">{group.name}</span>
                          {expandedGroups[group.name] ? 
                            <ChevronDown className="h-4 w-4" /> : 
                            <ChevronRight className="h-4 w-4" />
                          }
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="space-y-1 p-1">
                            {group.lessons.map((lesson) => (
                              <div
                                key={lesson.id}
                                className={`p-2 rounded-md cursor-pointer flex items-center ${
                                  lesson.id === currentLesson?.id
                                    ? "bg-primary/10 text-primary"
                                    : "hover:bg-gray-100"
                                }`}
                                onClick={() => selectLesson(lesson)}
                              >
                                <div className="mr-2 w-6 h-6 flex items-center justify-center rounded-full bg-gray-200">
                                  <Play className="h-3 w-3" />
                                </div>
                                <span className="line-clamp-1">{lesson.title}</span>
                              </div>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    ))
                  ) : (
                    lessons.map((lesson) => (
                      <div
                        key={lesson.id}
                        className={`p-2 rounded-md cursor-pointer flex items-center ${
                          lesson.id === currentLesson?.id
                            ? "bg-primary/10 text-primary"
                            : "hover:bg-gray-100"
                        }`}
                        onClick={() => selectLesson(lesson)}
                      >
                        <div className="mr-2 w-6 h-6 flex items-center justify-center rounded-full bg-gray-200">
                          <Play className="h-3 w-3" />
                        </div>
                        <span className="line-clamp-1">{lesson.title}</span>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CourseLesson;
